import React, { useCallback, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { Tabs, useAppState, useAutoAdjustBrightness } from '@sg/garnish';

import {
  ScanToPay,
  useScanToPayContent,
  useScanToPayState,
  useScanToPayTabs,
} from '@order/features/scan-to-pay';
import { CreditCardForm } from '@order/integrations/stripe';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useLoyaltyPremiumStatus } from '../../hooks';
import {
  useScanToPayNavigation,
  useScanToPayNotifications,
  useScanToPayTelemetry,
} from './hooks';

export const ScanToPayScreen = () => {
  const isLoggedIn = useIsLoggedIn();

  const isForgotToScanEnabled = useFeatureFlag('CELS-4258-forgot-to-scan-enabled'); // prettier-ignore
  const isAutoAdjustBrightnessEnabled = useFeatureFlag('CELS-4673-auto-adjust-brightness-enabled'); // prettier-ignore
  const isStripeSdkEnabled = useFeatureFlag('CELS-4707-scan-to-pay-stripe-sdk-enabled'); // prettier-ignore

  const {
    activeTabItemId,
    scanAndPayTab,
    scanOnlyTab,
    tabs,
    setActiveTabItemId,
  } = useScanToPayTabs();

  // ─── Machine ───────────────────────────────────────────────────────────────

  const {
    availableCredit,
    availablePoints,
    pointsRewards,
    paymentMethodId,
    paymentMethods,
    paymentToken,
    identifyToken,
    useCredit,
    isScanToPayFailed,
    isFetchingData,
    isAddingPaymentMethod,
    isSelectingPaymentMethod,
    isSettingDefaultPaymentMethod,
    isShowingLoyaltyInfo,
    isShowingFaq,
    start,
    cancelRequest,
    requestAddPayment,
    requestPaymentSelection,
    switchPaymentMethod,
    switchUseCredit,
    requestLoyaltyInfo,
    refetchCredits,
    requestFaq,
  } = useScanToPayState();

  // ─── Contentful ────────────────────────────────────────────────────────────

  const {
    isFetchingContent,
    scanAndPayTitle,
    scanOnlyTitle,
    emptyStateImageUrl,
    emptyStateDescription,
    benefitsDisclaimer,
    faq,
    hexagonSize,
    qrCodeSize,
  } = useScanToPayContent();

  // ─── Flags ─────────────────────────────────────────────────────────────────

  const isLoading = isFetchingData || isFetchingContent;
  const shouldShowQrCode = !isLoading && paymentToken && !isScanToPayFailed; // prettier-ignore
  const shouldShowEmptyState = !isLoading && !paymentToken && !isScanToPayFailed; // prettier-ignore
  const shouldShowScanOnlyState = !isLoading && identifyToken && !isScanToPayFailed; // prettier-ignore
  const shouldShowPaymentSelectionModal = isSelectingPaymentMethod || isSettingDefaultPaymentMethod; // prettier-ignore

  // The `useAppState` hook will restart the machine before the mutation is complete.
  // This flag prevents that behavior so it will only be restarted once the data is up to date.
  const [isUpdatingPaymentMethod, setIsUpdatingPaymentMethod] = useState(false);

  const { isPremium } = useLoyaltyPremiumStatus();
  const palette = isPremium ? 'dark-kale' : 'cream';

  // ─── Navigation ────────────────────────────────────────────────────────────

  const { goToSgRewards, goToForgotToScan, goToSupport, goToPayments, goBack } =
    useScanToPayNavigation(cancelRequest);

  const handlePaymentSelectionRequest = useCallback(() => {
    if (isStripeSdkEnabled) {
      setIsUpdatingPaymentMethod(true);
      goToPayments();
      return;
    }

    requestPaymentSelection();
  }, [goToPayments, isStripeSdkEnabled, requestPaymentSelection]);

  // ─── Initialization ────────────────────────────────────────────────────────

  const startMachine = useCallback(() => {
    if (!isLoggedIn) return;
    setIsUpdatingPaymentMethod(false);

    start();
  }, [isLoggedIn, start]);

  const onVisible = useCallback(() => {
    if (!isLoggedIn) return;

    if (isUpdatingPaymentMethod) {
      setIsUpdatingPaymentMethod(false);
      return;
    }

    startMachine();
  }, [startMachine, isUpdatingPaymentMethod, isLoggedIn]);

  useFocusEffect(startMachine);
  useAppState({ onVisible });

  // ─── Push Notifications ──────────────────────────────────────────────

  useScanToPayNotifications({ onOrderCompleted: refetchCredits });

  // ─── Telemetry ─────────────────────────────────────────────────────────────

  const {
    pointsTapped,
    goToRewardsTapped,
    faqTapped,
    contactUsTapped,
    forgotToScanTapped,
    addCardTapped,
    addCardSuccess,
    onSwitchUseCredit,
    changePaymentTapped,
    changePaymentAddCardTapped,
    changePaymentSuccess,
  } = useScanToPayTelemetry({
    isPremium,
    isOnScanAndPayTab: activeTabItemId === scanAndPayTab.id,
    goToSgRewards,
    goToForgotToScan,
    goToSupport,
    requestAddPayment,
    onPaymentMethodAdded: start,
    requestPaymentSelection: handlePaymentSelectionRequest,
    switchPaymentMethod,
    switchUseCredit,
    requestLoyaltyInfo,
    requestFaq,
  });

  // ─── Auto Adjust Brightness ────────────────────────────────────────────────

  useAutoAdjustBrightness({
    isEnabled: isAutoAdjustBrightnessEnabled,
    brightness: 1,
  });

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <ScanToPay.TabsContainer palette={palette}>
        <ScanToPay.TabsHeader palette={palette} onClose={goBack} />

        <Tabs.Bar
          palette={palette}
          tabs={tabs}
          activeTabId={activeTabItemId}
          onChange={setActiveTabItemId}
        />

        <ScanToPay.Tab
          isActive={activeTabItemId === scanAndPayTab.id}
          tabId={scanAndPayTab.id}
          panelId={scanAndPayTab.panelId}
        >
          <ScanToPay.Container>
            {isScanToPayFailed && !isLoading ? (
              <ScanToPay.ErrorState palette={palette} onRefresh={start} />
            ) : null}

            {isLoading ? (
              <ScanToPay.LoadingState
                palette={palette}
                title={scanAndPayTitle}
                isLoadingSize={isFetchingContent}
                hexagonSize={hexagonSize}
              />
            ) : null}

            {shouldShowQrCode ? (
              <>
                <ScanToPay.QrCode
                  palette={palette}
                  title={scanAndPayTitle}
                  qrCode={paymentToken}
                  hexagonSize={hexagonSize}
                  qrCodeSize={qrCodeSize}
                />

                <ScanToPay.BottomCard palette={palette}>
                  <ScanToPay.PaymentMethod
                    palette={palette}
                    paymentMethodId={paymentMethodId}
                    paymentMethods={paymentMethods}
                    onPaymentMethodSelection={changePaymentTapped}
                  />

                  <ScanToPay.CreditBalance
                    palette={palette}
                    shouldAllowToggle
                    useCredit={useCredit}
                    creditBalance={availableCredit}
                    onToggleUseCredit={onSwitchUseCredit}
                  />
                </ScanToPay.BottomCard>
              </>
            ) : null}

            {shouldShowEmptyState ? (
              <>
                <ScanToPay.EmptyState
                  palette={palette}
                  imageUrl={emptyStateImageUrl}
                  body={emptyStateDescription}
                  onAddCard={addCardTapped}
                />

                <ScanToPay.BottomCard palette={palette}>
                  <ScanToPay.CreditBalance
                    palette={palette}
                    shouldAllowToggle={false}
                    useCredit={useCredit}
                    creditBalance={availableCredit}
                    onToggleUseCredit={onSwitchUseCredit}
                  />
                </ScanToPay.BottomCard>
              </>
            ) : null}
          </ScanToPay.Container>

          {isLoading || isScanToPayFailed ? null : (
            <ScanToPay.Footer palette={palette}>
              <ScanToPay.PointsCta
                palette={palette}
                points={availablePoints}
                onPress={pointsTapped}
              />

              {faq.length > 0 ? (
                <ScanToPay.FaqCta palette={palette} onPress={faqTapped} />
              ) : null}
            </ScanToPay.Footer>
          )}
        </ScanToPay.Tab>

        <ScanToPay.Tab
          isActive={activeTabItemId === scanOnlyTab.id}
          tabId={scanOnlyTab.id}
          panelId={scanOnlyTab.panelId}
        >
          <ScanToPay.Container>
            {isScanToPayFailed && !isLoading ? (
              <ScanToPay.ErrorState palette={palette} onRefresh={start} />
            ) : null}

            {isLoading ? (
              <ScanToPay.LoadingState
                palette={palette}
                title={scanOnlyTitle}
                isLoadingSize={isFetchingContent}
                hexagonSize={hexagonSize}
              />
            ) : null}

            {shouldShowScanOnlyState ? (
              <ScanToPay.QrCode
                palette={palette}
                title={scanOnlyTitle}
                qrCode={identifyToken}
                hexagonSize={hexagonSize}
                qrCodeSize={qrCodeSize}
              />
            ) : null}
          </ScanToPay.Container>

          {isLoading || isScanToPayFailed ? null : (
            <ScanToPay.Footer palette={palette}>
              <ScanToPay.PointsCta
                palette={palette}
                points={availablePoints}
                onPress={pointsTapped}
              />

              {faq.length > 0 ? (
                <ScanToPay.FaqCta palette={palette} onPress={faqTapped} />
              ) : null}
            </ScanToPay.Footer>
          )}
        </ScanToPay.Tab>
      </ScanToPay.TabsContainer>

      <CreditCardForm.AddModal
        visible={isAddingPaymentMethod}
        onRequestClose={cancelRequest}
        onSuccess={addCardSuccess}
      />

      {!isLoading && paymentMethodId ? (
        <ScanToPay.PaymentSelectionModal
          isVisible={shouldShowPaymentSelectionModal}
          isLoading={isSettingDefaultPaymentMethod}
          paymentMethodId={paymentMethodId}
          paymentMethods={paymentMethods}
          onSaveChanges={changePaymentSuccess}
          onAddPaymentMethod={changePaymentAddCardTapped}
          onRequestClose={cancelRequest}
        />
      ) : null}

      <ScanToPay.PointsModal
        pointsRewards={pointsRewards}
        isVisible={isShowingLoyaltyInfo}
        disclaimer={benefitsDisclaimer}
        refetchRewards={start}
        onGoToSgRewards={goToRewardsTapped}
        onRequestClose={cancelRequest}
      />

      <ScanToPay.FaqModal
        faq={faq}
        isVisible={isShowingFaq}
        onForgotToScan={isForgotToScanEnabled ? forgotToScanTapped : undefined}
        onContactSupport={contactUsTapped}
        onRequestClose={cancelRequest}
      />
    </>
  );
};
